import {UniversalLoginInstance} from 'app/modules/auth/types/universalLogin'

import {getUniversalLoginErrorMessage, isUniversalLoginConflict} from './universalLoginResponse'

export type AiLoginErrorStep = 'account_not_found' | 'use_email_password'

export type AiLoginErrorResponse = {
  message?: string
  statusCode?: number
  result?: {
    step?: string
    email?: string
    instances?: UniversalLoginInstance[]
  }
}

export type AiLoginErrorState = {
  step: AiLoginErrorStep
  message: string
  email?: string
}

export function getAiLoginErrorStep(response?: AiLoginErrorResponse | null): string | undefined {
  return response?.result?.step
}

export function isAccountNotFoundError(response?: AiLoginErrorResponse | null): boolean {
  return getAiLoginErrorStep(response) === 'account_not_found'
}

export function isUseEmailPasswordError(response?: AiLoginErrorResponse | null): boolean {
  return getAiLoginErrorStep(response) === 'use_email_password'
}

export type AiLoginErrorHandlers = {
  onAccountNotFound: (message: string) => void
  onUseEmailPassword: (message: string, email?: string) => void
  onSchoolPickerConflict?: (response: AiLoginErrorResponse) => void
  onDefaultError: (message: string) => void
}

export type AiLoginErrorOutcome = 'conflict' | 'handled' | 'default'

/**
 * Shared handler for AI universal + tenant login error steps.
 */
export function handleAiLoginError(
  response: AiLoginErrorResponse,
  handlers: AiLoginErrorHandlers,
  fallbackMessage = 'Login failed'
): AiLoginErrorOutcome {
  if (isUniversalLoginConflict(response)) {
    handlers.onSchoolPickerConflict?.(response)
    return 'conflict'
  }

  const step = getAiLoginErrorStep(response)

  switch (step) {
    case 'account_not_found':
      handlers.onAccountNotFound(response.message || 'No account found. Please Sign Up.')
      return 'handled'
    case 'use_email_password':
      handlers.onUseEmailPassword(
        response.message || 'Please sign in with your email and password.',
        response.result?.email
      )
      return 'handled'
    default:
      handlers.onDefaultError(
        getUniversalLoginErrorMessage(response.message, response.statusCode, fallbackMessage)
      )
      return 'default'
  }
}
